import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Mail, Printer, CheckCircle2, Clock, Utensils, Armchair } from "lucide-react";
import api, { errText, qrImage } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Badge } from "@/components/Badge";

export default function Ticket() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [diet, setDiet] = useState(null);
  const [dietBusy, setDietBusy] = useState(false);

  const saveDietary = async () => {
    setDietBusy(true);
    try {
      const { data: res } = await api.post(`/public/registrations/${token}/dietary`, { dietary: diet });
      setDiet(res.dietary);
      setData((d) => ({ ...d, registration: { ...d.registration, dietary: res.dietary } }));
      toast.success(res.dietary ? "Thanks — noted for the kitchen" : "Dietary note cleared");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setDietBusy(false);
    }
  };

  useEffect(() => {
    api
      .get(`/public/registrations/${token}`)
      .then(({ data }) => {
        setData(data);
        setDiet(data.registration.dietary || "");
      })
      .catch((e) => setError(errText(e)));
  }, [token]);

  const resend = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/registrations/${token}/resend`);
      toast.success(r.kind === "invite" ? `Invitation re-sent to ${r.to}` : `Pass re-sent to ${r.to}`);
      if (!r.whatsapp_enabled && data.registration.phone)
        toast.info("WhatsApp delivery is not configured yet — email sent instead.");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (error)
    return (
      <Shell>
        <h1 className="font-display text-3xl font-black uppercase" data-testid="ticket-error">
          {error}
        </h1>
        <Link to="/retrieve-qr" className="btn-secondary mt-6">
          Look up my pass
        </Link>
      </Shell>
    );
  if (!data)
    return (
      <Shell>
        <p className="text-sm text-slate-500">Loading pass…</p>
      </Shell>
    );

  const { registration: r, event } = data;
  const page = data.pass_page || {};
  const rsvpPage = data.rsvp_page || {};
  const waitlisted = r.status === "waitlisted";
  const held = !waitlisted && !data.pass_available;
  const needsRsvp = held && data.rsvp_required && r.rsvp !== "yes";
  const fill = (text) =>
    (text || "")
      .replaceAll("{first_name}", r.first_name || "")
      .replaceAll("{last_name}", r.last_name || "")
      .replaceAll("{salutation}", r.salutation || "")
      .replaceAll("{salutation_name}", r.salutation ? `${r.salutation} ${r.last_name}` : r.first_name)
      .replaceAll("{event_name}", event.name || "")
      .replaceAll("{date}", event.date || "")
      .replaceAll("{time}", event.start_time || "")
      .replaceAll("{venue}", event.venue || "")
      .replaceAll("{host_org}", event.host_org || "")
      .replaceAll("{reference}", r.registration_code || "")
      .replaceAll("{email}", r.email || "");
  const noteRows = [
    ["Dress code", page.dress_code],
    ["Parking", page.parking],
    ["Arrival time", page.arrival_time],
  ].filter(([, v]) => (v || "").trim());
  const freeNotes = (page.notes || "").trim();
  const showNotes = !held && !waitlisted && (noteRows.length > 0 || freeNotes);

  return (
    <Shell>
      <div className="rise" data-testid="ticket-page">
        <p className="overline">
          {waitlisted ? page.waitlist_label : held ? page.held_label : page.status_label}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mt-2">
          {event.name}
        </h1>

        <div className="grid md:grid-cols-[320px_1fr] gap-8 mt-8 items-start">
          <div className="card p-6 text-center">
            {waitlisted ? (
              <div className="py-14" data-testid="ticket-waitlist-notice">
                <Clock className="mx-auto text-[#ffb800]" size={40} />
                <p className="text-sm text-slate-300 mt-4">{page.waitlist_note}</p>
              </div>
            ) : held ? (
              <div className="py-12" data-testid="ticket-held-notice">
                <Clock className="mx-auto text-[#ffb800]" size={40} />
                <p className="text-sm text-slate-300 mt-4">
                  {needsRsvp ? page.needs_rsvp_note : page.held_note}
                </p>
                {needsRsvp && (
                  <Link to={`/rsvp/${token}`} className="btn-primary mt-5" data-testid="ticket-rsvp-link">
                    Reply to my invitation
                  </Link>
                )}
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl p-4 inline-block">
                  <img src={qrImage(r.qr_token)} alt="QR pass" className="w-56 h-56" data-testid="ticket-qr-image" />
                </div>
                <p className="font-mono2 text-xs text-slate-400 mt-4" data-testid="ticket-code">
                  REF {r.registration_code}
                </p>
              </>
            )}
            {r.checked_in && (
              <p
                className="mt-4 inline-flex items-center gap-2 text-xs font-mono2 text-[#00e599] border border-[#00e599]/30 rounded-full px-3 py-1"
                data-testid="ticket-checked-in-pill"
              >
                <CheckCircle2 size={13} /> Checked in
              </p>
            )}
          </div>

          <div>
            <div className="card p-6">
              <p className="label">{page.person_label}</p>
              <p className="text-xl font-bold" data-testid="ticket-attendee-name">
                {[r.salutation, r.first_name, r.last_name].filter(Boolean).join(" ")}
              </p>
              <p className="text-sm text-slate-400">{r.email}</p>
              {r.company && (
                <p className="text-sm text-slate-400 mt-1">
                  {r.job_title ? `${r.job_title}, ` : ""}
                  {r.company}
                </p>
              )}
              <div className="border-t border-[#252f3e] mt-5 pt-5 space-y-2.5 text-sm text-slate-300">
                <p className="flex items-center gap-2">
                  <CalendarDays size={14} className="text-[#00e599]" /> {event.date} · {event.start_time}
                  {event.end_time ? `–${event.end_time}` : ""}
                </p>
                <p className="flex items-center gap-2">
                  <MapPin size={14} className="text-[#00e599]" /> {event.venue}
                </p>
                {!held && !waitlisted && (r.table || "").trim() && (
                  <p className="flex items-center gap-2" data-testid="ticket-table">
                    <Armchair size={14} className="text-[#00e599]" /> Table{" "}
                    <span className="font-bold text-white">{r.table}</span>
                  </p>
                )}
              </div>
            </div>

            {showNotes && (
              <div className="card p-6 mt-5" data-testid="ticket-notes">
                <p className="label">{fill(page.notes_heading) || "Before you arrive"}</p>
                {noteRows.length > 0 && (
                  <div className="mt-3 space-y-2 text-sm">
                    {noteRows.map(([label, value]) => (
                      <div key={label} className="flex flex-wrap gap-x-3">
                        <span className="text-slate-500 w-28 shrink-0">{label}</span>
                        <span className="text-slate-300" data-testid={`ticket-note-${label.split(" ")[0].toLowerCase()}`}>
                          {fill(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {freeNotes && (
                  <p className="text-sm text-slate-300 mt-3 whitespace-pre-line" data-testid="ticket-note-extra">
                    {fill(freeNotes)}
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-5">              <button onClick={resend} disabled={busy} className="btn-secondary" data-testid="ticket-resend-btn">
                <Mail size={15} /> {busy ? "Sending…" : held ? "Resend my invitation" : page.resend_label}
              </button>
              {!waitlisted && !held && (
                <button onClick={() => window.print()} className="btn-secondary" data-testid="ticket-print-btn">
                  <Printer size={15} /> {page.print_label}
                </button>
              )}
              {!held && !waitlisted && page.show_survey === true && (
                <Link to={`/survey/${r.qr_token}`} className="btn-ghost px-2" data-testid="ticket-survey-link">
                  {page.survey_label}
                </Link>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-4">{page.privacy_note}</p>

            {!waitlisted && page.show_dietary === true && (
              <div className="border-t border-[#252f3e] mt-6 pt-5" data-testid="ticket-dietary">
                <label className="label">{rsvpPage.dietary_label}</label>
                <p className="text-xs text-slate-500 mt-1">{rsvpPage.dietary_hint}</p>
                <div className="flex flex-wrap gap-3 mt-3">
                  <input
                    className="input flex-1 min-w-[220px]"
                    maxLength={300}
                    placeholder="e.g. vegetarian, no nuts"
                    data-testid="ticket-dietary-input"
                    value={diet ?? ""}
                    onChange={(e) => setDiet(e.target.value)}
                  />
                  <button
                    onClick={saveDietary}
                    disabled={dietBusy}
                    className="btn-secondary"
                    data-testid="ticket-dietary-save-btn"
                  >
                    <Utensils size={15} /> {dietBusy ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {!waitlisted && !held && page.show_badge !== false && (
          <div className="mt-10">
            <p className="label">{page.badge_heading}</p>
            <Badge registration={r} event={event} />
          </div>
        )}
      </div>
    </Shell>
  );
}
